// =============================================================================
// ApHomeScreen — /ap (T-331). The clean "AP / Spend" home: three zones.
//   1. Add spend     — Bill vs Paid, then upload / snap / enter manually.
//   2. Needs you     — receipts extracted but not yet coded (low confidence first).
//   3. Coded (Done)  — the tracker: every coded invoice with state + coding + filters.
// The actual review/coding happens in the upgraded flows (/ap/receipts modal,
// /ap/new form); this is the front door + the "track it" view.
// =============================================================================

import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft, Plus, Upload, Camera, Receipt, FileText, CreditCard,
  Banknote, ChevronRight, Fuel, Building2, Zap, Users,
} from "lucide-react";
import { supabase, apiFetch } from "@/lib/supabase";
import { useRequireAuth } from "@/lib/auth";
import { Card, Pill, Button, KPI, Spinner, EmptyState } from "@/components/ui";
import { fmtMoney } from "@/lib/dates";

type DoneTab = "all" | "pending" | "in_xero" | "paid";
const DONE_STATUSES: Record<DoneTab, string[] | null> = {
  all: null,
  pending: ["captured", "approved"],
  in_xero: ["entered_in_xero"],
  paid: ["paid"],
};

function statusPill(status: string): { label: string; tone: any } {
  switch (status) {
    case "paid":            return { label: "Paid", tone: "ok" };
    case "entered_in_xero": return { label: "In Xero", tone: "bid" };
    case "approved":        return { label: "Approved", tone: "bid" };
    case "captured":        return { label: "Captured", tone: "default" };
    case "void":            return { label: "Void", tone: "critical" };
    case "disputed":        return { label: "Disputed", tone: "warn" };
    default:                return { label: status, tone: "default" };
  }
}

function payBadge(pm: string | null): string | null {
  if (!pm) return null;
  if (pm === "card_credit") return "Credit card";
  if (pm === "card_debit")  return "Debit card";
  if (pm === "reimbursement") return "Reimbursement";
  if (pm === "eft") return "EFT";
  return null;
}

// T-488 — connection state and the one-time authorise. The start endpoint hands
// back a URL rather than redirecting, because a fetch carrying a bearer token
// can't follow a 302 to another origin — so we ask for the URL and navigate.
function XeroConnection() {
  const [busy, setBusy] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState<string | null>(null);
  const qc = useQueryClient();
  const { data: status } = useQuery({
    queryKey: ["xero_status"],
    queryFn: async () => {
      const res = await apiFetch("/xero/status");
      if (!res.ok) return null;
      return await res.json();
    },
    staleTime: 60_000,
  });

  async function connect() {
    setBusy(true);
    try {
      const res = await apiFetch("/xero/oauth/start");
      const body = await res.json();
      if (!res.ok || !body?.url) throw new Error(body?.detail ?? body?.error ?? "Could not start the Xero connection.");
      window.location.href = body.url;
    } catch (e: any) {
      window.alert(e?.message ?? String(e));
      setBusy(false);
    }
  }

  // T-503 — pull the chart of accounts from Xero on demand. Adding an account in
  // Xero used to need a migration before ops could code to it; now it needs this
  // button. Xero owns the CoA, this only mirrors it.
  async function syncAccounts() {
    setSyncing(true); setSyncMsg(null);
    try {
      const res = await apiFetch("/xero/accounts/sync", { method: "POST" });
      const body = await res.json().catch(() => ({} as any));
      if (!res.ok) {
        throw new Error(
          (Array.isArray(body?.messages) && body.messages[0]) ||
          body?.detail || body?.error || `Sync failed (HTTP ${res.status}).`);
      }
      setSyncMsg(`${body.usable_for_ap} accounts available for coding` +
        (body.retired ? ` · ${body.retired} retired` : ""));
      qc.invalidateQueries({ queryKey: ["xero_accounts"] });
      qc.invalidateQueries({ queryKey: ["xero_accounts_push"] });
      qc.invalidateQueries({ queryKey: ["xero_bank_accounts"] });
    } catch (e: any) {
      setSyncMsg(e?.message ?? String(e));
    } finally {
      setSyncing(false);
    }
  }

  if (status?.is_connected) {
    return (
      <div className="text-right text-[11px] text-text-muted">
        <div className="text-ok">Xero connected</div>
        <div>{status.tenant_name}</div>
        <button type="button" onClick={syncAccounts} disabled={syncing}
          title="Re-read the chart of accounts from Xero — do this after adding an account there"
          className="mt-0.5 text-accent hover:underline disabled:opacity-50">
          {syncing ? "Syncing accounts…" : "Sync chart of accounts"}
        </button>
        {syncMsg && <div className="mt-0.5 text-text-dim max-w-[220px]">{syncMsg}</div>}
      </div>
    );
  }
  return (
    <Button onClick={connect} disabled={busy} className="shrink-0">
      {busy ? "Opening Xero…" : "Connect Xero"}
    </Button>
  );
}

export default function ApHomeScreen() {
  useRequireAuth();
  const [intent, setIntent] = useState<"bill" | "paid">("bill");
  const [tab, setTab] = useState<DoneTab>("all");

  // Zone 2 — receipts awaiting coding (lowest confidence first).
  const { data: needs = [], isLoading: needsLoading } = useQuery({
    queryKey: ["ap_needs_review"],
    queryFn: async () => {
      const { data } = await supabase
        .from("pending_receipts")
        .select("receipt_id, supplier_guess, amount_guess, ocr_confidence, ocr_processed_at, captured_at")
        .eq("status", "new")
        .order("ocr_confidence", { ascending: true, nullsFirst: true })
        .limit(50);
      return (data ?? []) as any[];
    },
  });

  // KPIs + pipeline stage counts across all invoices.
  const { data: kpi } = useQuery({
    queryKey: ["ap_home_kpi"],
    queryFn: async () => {
      const { data } = await supabase.from("ap_invoices").select("ap_invoice_id, status, amount_inc_gst, paid_date, due_date, xero_entry_type, is_reimbursement, xero_bill_id");
      const rows = (data ?? []) as any[];
      const today = new Date().toISOString().slice(0, 10);
      const monthStart = today.slice(0, 7) + "-01";

      // -----------------------------------------------------------------------
      // G4 — these counts have to agree with the screens they point at, and
      // reimbursements now travel a different road.
      //
      // A reimbursement leaves Code and waits on /ap/reimbursements to be
      // bundled. It is NOT ready to push (it never goes through Push to Xero)
      // and it is NOT a bill owing until the bundled bill actually exists.
      // Counting it in either stage sends you to a screen that doesn't list it.
      // -----------------------------------------------------------------------
      const isReimb  = (r: any) => !!r.is_reimbursement;
      const isBilled = (r: any) => !!r.xero_bill_id;

      // Waiting to be pushed to Xero — supplier spend only.
      const isReady = (r: any) =>
        ["captured", "approved"].includes(r.status) && !isReimb(r);

      // T-483 — owing means approved supplier bills, in Xero or not yet.
      // G4 — plus reimbursements, but only once they ARE a bill.
      const isOwing = (r: any) =>
        r.xero_entry_type === "bill"
        && ["approved", "entered_in_xero"].includes(r.status)
        && (!isReimb(r) || isBilled(r));

      // One Xero bill = one bill owing, however many receipts are on it.
      const billKeys = (list: any[]) =>
        new Set(list.map(r => r.xero_bill_id ?? r.ap_invoice_id)).size;

      const owing = rows.filter(isOwing);

      return {
        pending_value: rows.filter(r => ["captured", "approved"].includes(r.status)).reduce((a, r) => a + Number(r.amount_inc_gst ?? 0), 0),
        in_xero_value: rows.filter(r => r.status === "entered_in_xero").reduce((a, r) => a + Number(r.amount_inc_gst ?? 0), 0),
        paid_mtd: rows.filter(r => r.status === "paid" && r.paid_date && r.paid_date >= monthStart).reduce((a, r) => a + Number(r.amount_inc_gst ?? 0), 0),
        // Pipeline stage counts
        ready_count:  rows.filter(isReady).length,
        bills_count:  billKeys(owing),
        overdue_count: billKeys(owing.filter(r => r.due_date && r.due_date < today)),
        // Owed to staff, not yet bundled — the Reimburse stage.
        reimb_count: rows.filter(r =>
          isReimb(r) && !isBilled(r)
          && ["captured", "pending_review", "approved", "entered_in_xero"].includes(r.status)).length,
      };
    },
  });

  // Zone 3 — coded invoices (the tracker).
  const { data: invoices = [], isLoading: invLoading } = useQuery({
    queryKey: ["ap_home_invoices", tab],
    queryFn: async () => {
      let q = supabase
        .from("ap_invoices")
        .select("ap_invoice_id, supplier_name, amount_inc_gst, status, budget_code, ftc_class, payment_method, project_id, invoice_date, captured_at")
        .order("captured_at", { ascending: false })
        .limit(100);
      const f = DONE_STATUSES[tab];
      if (f) q = q.in("status", f as any);
      const { data } = await q;
      const rows = (data ?? []) as any[];
      const pids = Array.from(new Set(rows.map(r => r.project_id).filter(Boolean)));
      if (pids.length === 0) return rows;
      const { data: ps } = await supabase.from("projects").select("project_id, project_code").in("project_id", pids);
      const byId = new Map((ps ?? []).map((p: any) => [p.project_id, p.project_code]));
      return rows.map(r => ({ ...r, project_code: byId.get(r.project_id) ?? null }));
    },
  });

  const doneTabs: { key: DoneTab; label: string }[] = [
    { key: "all", label: "All" },
    { key: "pending", label: "Pending" },
    { key: "in_xero", label: "In Xero" },
    { key: "paid", label: "Paid" },
  ];

  return (
    <div className="p-4 md:p-6 max-w-[1100px] mx-auto">
      <Link to="/finance" className="inline-flex items-center gap-1.5 text-[12px] text-text-muted hover:text-accent mb-3">
        <ArrowLeft size={14}/> Finance hub
      </Link>
      <div className="mb-5 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">AP / Spend</h1>
          <div className="text-text-muted text-[13px] mt-1">Capture a receipt or bill, let the AI code it, you supervise. Track it all here.</div>
        </div>
        <XeroConnection />
      </div>

      <div className="grid grid-cols-3 gap-3 mb-4">
        <KPI label="Pending spend" value={fmtMoney(kpi?.pending_value ?? 0)} sub="coded, not yet in Xero" />
        <KPI label="In Xero" value={fmtMoney(kpi?.in_xero_value ?? 0)} sub="entered, awaiting payment" />
        {/* "Marked paid" is the honest label: paid_date is stamped the day the
            bill is ticked off in-app (bank-rec matches use the real bank date),
            so backlog cleanup piles into the month it was processed. */}
        <KPI label="Marked paid this month" value={fmtMoney(kpi?.paid_mtd ?? 0)} sub="incl. card spends + backlog cleanup" />
      </div>

      {/* -----------------------------------------------------------------
          THE FLOW — corrected 21 Aug 2026
          -----------------------------------------------------------------
          It used to draw six boxes in a straight line, which said three things
          that are not true: that Push to Xero and Reimburse happen one after
          the other, that Bank rec is a stage of paying a bill, and that Full
          queue is somewhere a bill goes.

          What actually happens, in James's words:

            "Receipt lands, worker creates it in Ops, we code it and approve it.
             If Spend Money -> To Xero queue. If Bill -> To Xero. If
             Reimbursement -> Reimbursement queue, and once bundled it pushes
             the bill into Xero. So Bills to Pay should all be in Xero. In Bills
             to Pay we track and pay in NAB banking and record the payment in
             Ops. When that payment hits Xero there's a bill waiting to
             reconcile against."

          So: one fork, then one destination. Bank rec is not in the flow at all
          - "not really being used, as we have the workers and Xero reconciles
          any pending bills with actual account transactions." It keeps its
          screen; it stops being drawn as a step.
      ------------------------------------------------------------------ */}
      <Card className="mb-5">
        <div className="p-3 md:p-4">
          <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-semibold mb-2.5 px-1">The flow</div>

          <div className="flex items-stretch gap-1.5 overflow-x-auto">
            <FlowStage to="/ap/receipts" icon={Receipt} label="Code" sub="read & code"
                       count={needs.length} tone="accent" />

            <FlowArrow />

            {/* The fork. A coded bill goes ONE of these two ways, never both -
                which is also why a reimbursement is excluded from the Push to
                Xero queue: two buttons that both send the same receipt would
                pay somebody twice. */}
            <div className="flex-1 min-w-[150px] rounded-lg border border-dashed border-line p-1.5 flex flex-col gap-1">
              <FlowStage to="/ap/xero" icon={Upload} label="Push to Xero" sub="suppliers + card spend"
                         count={kpi?.ready_count ?? 0} tone="accent" inner />
              <div className="text-[9px] uppercase tracking-wider2 text-text-dim text-center leading-none">or</div>
              <FlowStage to="/ap/reimbursements" icon={Users} label="Reimburse" sub="staff, bundled"
                         count={kpi?.reimb_count ?? 0} tone="muted" inner />
            </div>

            <FlowArrow />

            <FlowStage to="/ap/bills" icon={Banknote} label="Bills to pay" sub="in Xero, owing"
                       count={kpi?.bills_count ?? 0}
                       tone={(kpi?.overdue_count ?? 0) > 0 ? "danger" : "muted"}
                       dangerSub={`${kpi?.overdue_count} overdue`} />
          </div>

          <div className="text-[11px] text-text-muted mt-2.5 px-1">
            Both routes end in Xero, and nothing reaches <span className="text-text">Bills to pay</span> until
            it is there — so a payment can never go out against a bill Xero has not got.
            Pay in NAB, record it here, and the bill is waiting in Xero for the bank line.
          </div>

          <div className="text-[11px] text-text-dim mt-2 px-1 flex items-center gap-3 flex-wrap">
            <span>Also here:</span>
            <Link to="/ap/queue" className="hover:text-accent inline-flex items-center gap-1">
              <FileText size={11} /> Full queue
            </Link>
            <Link to="/ap/reconcile" className="hover:text-accent inline-flex items-center gap-1">
              <Zap size={11} /> Bank rec
            </Link>
            <span className="text-text-dim/70">— neither is a step; the queue is a view, and Xero does the reconciling.</span>
          </div>
        </div>
      </Card>

      {/* ZONE 1 — Add spend */}
      <Card>
        <div className="p-5">
          <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-semibold mb-3">Add spend</div>
          <div className="flex items-center gap-2 mb-4">
            <button type="button" onClick={() => setIntent("bill")}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[13px] font-medium border transition-colors ${intent === "bill" ? "bg-accent text-white border-accent" : "border-line text-text-muted hover:text-text"}`}>
              <FileText size={14}/> Bill to pay
            </button>
            <button type="button" onClick={() => setIntent("paid")}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[13px] font-medium border transition-colors ${intent === "paid" ? "bg-accent text-white border-accent" : "border-line text-text-muted hover:text-text"}`}>
              <CreditCard size={14}/> Already paid (card/EFT)
            </button>
          </div>
          <div className="text-[12px] text-text-muted mb-3">
            {intent === "bill"
              ? "An unpaid supplier bill — code it and it tracks here ready for Xero + payment."
              : "A receipt you've already paid on a card — code it for cost + FTC; the card sets the spend-money account (or a reimbursement bill)."}
          </div>
          <div className="flex gap-2 flex-wrap">
            <Link to="/ap/intake"><Button icon={Upload}>Upload receipt(s)</Button></Link>
            <Link to="/receipts"><Button variant="secondary" icon={Camera}>Snap on mobile</Button></Link>
            <Link to="/ap/new"><Button variant="secondary" icon={Plus}>Enter manually</Button></Link>
          </div>
        </div>
      </Card>

      {/* ZONE 2 — Needs your review */}
      <Card className="mt-4">
        <div className="p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-semibold">
              Needs your review {needs.length > 0 && <span className="text-accent">· {needs.length}</span>}
            </div>
            {needs.length > 0 && <Link to="/ap/receipts" className="text-[12px] text-accent hover:underline">Review all →</Link>}
          </div>
          {needsLoading ? (
            <div className="py-6 flex justify-center"><Spinner size={16}/></div>
          ) : needs.length === 0 ? (
            <div className="text-[13px] text-text-muted py-3">Nothing waiting — all caught up.</div>
          ) : (
            <div className="space-y-1.5">
              {needs.slice(0, 8).map((r: any) => (
                <Link key={r.receipt_id} to="/ap/receipts"
                  className="flex items-center gap-3 px-3 py-2 rounded-md border border-line bg-ink-raised hover:border-accent transition-colors">
                  <Receipt size={14} className="text-text-muted flex-shrink-0"/>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] text-text truncate">{r.supplier_guess ?? "No supplier read"}</div>
                    <div className="text-[11px] text-text-muted">
                      {r.amount_guess != null ? fmtMoney(r.amount_guess) : "—"}
                      {!r.ocr_processed_at && <span className="ml-2 text-warn">AI not run</span>}
                    </div>
                  </div>
                  {r.ocr_confidence != null && (
                    <Pill label={`AI ${Math.round(r.ocr_confidence * 100)}%`} tone={r.ocr_confidence >= 0.85 ? "ok" : "warning"} small/>
                  )}
                  <ChevronRight size={14} className="text-text-muted flex-shrink-0"/>
                </Link>
              ))}
            </div>
          )}
        </div>
      </Card>

      {/* ZONE 3 — Coded tracker */}
      <Card className="mt-4">
        <div className="p-5">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-semibold">Coded spend</div>
            <div className="inline-flex border border-line rounded-md bg-ink-raised p-0.5 gap-0.5">
              {doneTabs.map(t => (
                <button key={t.key} type="button" onClick={() => setTab(t.key)}
                  className={`px-3 py-1 rounded text-[12px] font-medium transition-colors ${tab === t.key ? "bg-accent text-white" : "text-text-muted hover:text-text"}`}>
                  {t.label}
                </button>
              ))}
            </div>
          </div>
          {invLoading ? (
            <div className="py-8 flex justify-center"><Spinner/></div>
          ) : invoices.length === 0 ? (
            <EmptyState icon={Receipt} title="Nothing coded yet" description="Coded invoices show here with their state, budget code and coding." />
          ) : (
            <div className="space-y-1.5">
              {(invoices as any[]).map((r) => {
                const sp = statusPill(r.status);
                const pb = payBadge(r.payment_method);
                return (
                  <div key={r.ap_invoice_id} className="flex items-center gap-3 px-3 py-2 rounded-md border border-line bg-ink-raised">
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] text-text truncate flex items-center gap-2">
                        {r.supplier_name ?? "—"}
                        {r.project_code && <span className="text-[11px] text-text-muted font-mono inline-flex items-center gap-1"><Building2 size={10}/>{r.project_code}</span>}
                      </div>
                      <div className="text-[11px] text-text-muted flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5">
                        {r.budget_code && <span className="font-mono">{r.budget_code}</span>}
                        {r.ftc_class && <span className="inline-flex items-center gap-1 text-accent"><Fuel size={10}/>{r.ftc_class.replace(/^FTC0?_/, "")}</span>}
                        {pb && <span>· {pb}</span>}
                        {r.invoice_date && <span>· {r.invoice_date}</span>}
                      </div>
                    </div>
                    <div className="text-[13px] text-text font-medium flex-shrink-0">{fmtMoney(r.amount_inc_gst ?? 0)}</div>
                    <Pill label={sp.label} tone={sp.tone} small/>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}

// -----------------------------------------------------------------------------
// One box in the flow strip. Pulled out of the map it used to live in so the
// fork can nest two of them inside one cell.
// -----------------------------------------------------------------------------
function FlowStage({
  to, icon: Icon, label, sub, count, tone, inner = false, dangerSub,
}: {
  to: string; icon: any; label: string; sub: string;
  count: number | null; tone: "accent" | "muted" | "danger";
  inner?: boolean; dangerSub?: string;
}) {
  const iconColour = tone === "danger" ? "text-danger" : tone === "accent" ? "text-accent" : "text-text-muted";
  return (
    <Link to={to}
      className={`${inner ? "" : "flex-1 min-w-[130px]"} rounded-lg border border-line bg-ink-raised hover:border-accent transition-colors px-3 ${inner ? "py-2" : "py-2.5"} block`}>
      <div className="flex items-center gap-2">
        <Icon size={15} className={iconColour} />
        <span className="text-[13px] font-medium text-text">{label}</span>
        {count != null && count > 0 && (
          <span className={`ml-auto text-[11px] font-mono font-semibold px-1.5 py-0.5 rounded ${
            tone === "danger" ? "bg-danger/15 text-danger" : "bg-accent/15 text-accent"}`}>{count}</span>
        )}
      </div>
      <div className="text-[10px] text-text-muted mt-1">
        {tone === "danger" && dangerSub ? dangerSub : sub}
      </div>
    </Link>
  );
}

function FlowArrow() {
  return <div className="flex items-center flex-shrink-0"><ChevronRight size={14} className="text-text-dim mx-0.5" /></div>;
}
